/* Public gallery — hero + items grouped by edition tag. Click any photo to zoom. */
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import PublicShell from "../../components/shell/PublicShell.jsx";
import { HeroBlock } from "../../components/SectionRenderer.jsx";
import { SmartImage, Skeleton, EmptyState, Icon } from "../../ui/index.js";
import { usePage, useGallery } from "../../data/hooks.js";

export default function GalleryPage() {
  const { sections } = usePage("gallery");
  const hero = sections.find((s) => s.type === "hero");
  const { gallery, loading } = useGallery();
  const [zoomed, setZoomed] = useState(null);

  // Group by editionTag, preserving the ordered query result.
  const groups = [];
  const byTag = {};
  for (const item of gallery) {
    const tag = item.editionTag || "Gallery";
    if (!byTag[tag]) { byTag[tag] = []; groups.push(tag); }
    byTag[tag].push(item);
  }

  return (
    <PublicShell>
      <Helmet><title>Gallery — Purple Angels Kids Foundation</title></Helmet>
      {hero ? <HeroBlock section={hero} /> : null}
      <section className="section">
        <div className="container">
          {loading ? (
            <div className="gallery-grid">{[0, 1, 2, 3].map((i) => <Skeleton key={i} h={200} />)}</div>
          ) : gallery.length === 0 ? (
            <EmptyState title="No photos yet" body="Images from our work will appear here." />
          ) : (
            groups.map((tag) => (
              <div key={tag} style={{ marginBottom: 48 }}>
                <h2 className="t-2xl" style={{ marginTop: 0, marginBottom: 20 }}>{tag}</h2>
                <div className="gallery-grid">
                  {byTag[tag].map((item) => (
                    <figure key={item.id} className="gallery-item">
                      <button
                        type="button"
                        className="gallery-item__frame"
                        onClick={() => item.image?.url && setZoomed(item)}
                        aria-label={`Zoom into ${item.image?.alt || item.caption || "photo"}`}
                      >
                        <SmartImage src={item.image?.url} alt={item.image?.alt || item.caption || ""} label={item.caption || ""} />
                      </button>
                      {item.caption && <figcaption className="t-sm muted" style={{ marginTop: 0, fontStyle: "italic" }}>{item.caption}</figcaption>}
                    </figure>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </section>
      {zoomed && <Lightbox item={zoomed} onClose={() => setZoomed(null)} />}
    </PublicShell>
  );
}

/* Full-size image overlay. Click the backdrop or press Escape to close. */
function Lightbox({ item, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div className="lightbox-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <button className="lightbox-close" onClick={onClose} aria-label="Close"><Icon.X size={20} /></button>
      <figure className="lightbox-figure" onClick={(e) => e.stopPropagation()}>
        <img src={item.image?.url} alt={item.image?.alt || item.caption || ""} />
        {item.caption && <figcaption className="lightbox-caption">{item.caption}</figcaption>}
      </figure>
    </div>
  );
}
